import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRemoteContent } from "@/lib/ContentContext";

const PRODUCT_ICONS = [
  "ri-fire-line",
  "ri-flight-takeoff-line",
  "ri-settings-3-line",
  "ri-flask-line",
  "ri-rocket-line",
  "ri-cpu-line",
];

const PRODUCT_STYLES = [
  {
    color: "from-cyan-500/20 to-transparent",
    accent: "text-cyan-400",
    border: "border-cyan-500/30 hover:border-cyan-400/60",
    bottom: "from-cyan-500",
  },
  {
    color: "from-orange-500/20 to-transparent",
    accent: "text-orange-400",
    border: "border-orange-500/30 hover:border-orange-400/60",
    bottom: "from-orange-500",
  },
  {
    color: "from-cyan-500/20 to-transparent",
    accent: "text-cyan-400",
    border: "border-cyan-500/30 hover:border-cyan-400/60",
    bottom: "from-cyan-500",
  },
  {
    color: "from-violet-500/20 to-transparent",
    accent: "text-violet-400",
    border: "border-violet-500/30 hover:border-violet-400/60",
    bottom: "from-violet-500",
  },
  {
    color: "from-rose-500/20 to-transparent",
    accent: "text-rose-400",
    border: "border-rose-500/30 hover:border-rose-400/60",
    bottom: "from-rose-500",
  },
  {
    color: "from-amber-500/20 to-transparent",
    accent: "text-amber-400",
    border: "border-amber-500/30 hover:border-amber-400/60",
    bottom: "from-amber-500",
  },
];

interface ProductsProps {
  lang: "zh" | "en";
}

interface ProductCardProps {
  prod: {
    zhTitle: string;
    zhDesc: string;
    enTitle: string;
    enDesc: string;
    image: string;
  };
  index: number;
  lang: "zh" | "en";
  hovered: number | null;
  onHover: (i: number | null) => void;
  onClick: () => void;
}

function ProductCard({ prod, index, lang, hovered, onHover, onClick }: ProductCardProps) {
  const style = PRODUCT_STYLES[index % PRODUCT_STYLES.length];
  const icon = PRODUCT_ICONS[index % PRODUCT_ICONS.length];
  const title = lang === "zh" ? prod.zhTitle : prod.enTitle;
  const desc = lang === "zh" ? prod.zhDesc : prod.enDesc;
  const isHovered = hovered === index;

  return (
    <div
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => onHover(null)}
      onClick={onClick}
      className={`relative group rounded-xl border bg-xh-bg-card overflow-hidden transition-all duration-400 cursor-pointer ${style.border} ${
        isHovered ? "scale-[1.02]" : ""
      }`}
    >
      {/* Image */}
      <div className="relative w-full h-44 overflow-hidden">
        <img
          src={prod.image}
          alt={title}
          className={`w-full h-full object-cover object-top transition-transform duration-500 ${
            isHovered ? "scale-110" : "scale-100"
          }`}
        />
        <div className={`absolute inset-0 bg-gradient-to-t ${style.color} opacity-80`} />
        <div className={`absolute top-3 left-3 w-9 h-9 flex items-center justify-center rounded-lg bg-xh-bg-panel/80 backdrop-blur-sm ${style.accent}`}>
          <i className={`${icon} text-lg`} />
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        <h3 className={`text-base font-bold mb-2 ${style.accent}`}>{title}</h3>
        <p className="text-gray-400 text-xs leading-relaxed mb-4">{desc}</p>

        {/* View link */}
        <div className={`flex items-center gap-1 text-xs font-semibold transition-all duration-300 ${style.accent} ${isHovered ? "gap-2" : ""}`}>
          <span>{lang === "zh" ? "查看产品" : "View Products"}</span>
          <i className="ri-arrow-right-line" />
        </div>
      </div>

      {/* Bottom accent line */}
      <div
        className={`absolute bottom-0 left-0 h-0.5 transition-all duration-400 bg-gradient-to-r ${style.bottom} to-transparent ${
          isHovered ? "w-full" : "w-0"
        }`}
      />
    </div>
  );
}

export default function Products({ lang }: ProductsProps) {
  const [hovered, setHovered] = useState<number | null>(null);
  const navigate = useNavigate();
  const siteContent = useRemoteContent();
  const products = siteContent.products;
  const detailProducts = siteContent.productsDetail;

  const categoryOrder = ["aero-engine", "uav-propulsion", "precision-mfg", "tech-services"];

  const goToMappedSubProject = (index: number) => {
    const categoryId = categoryOrder[index] ?? categoryOrder[0];
    if (index === 3) {
      navigate("/news");
      return;
    }
    const firstInCategory = detailProducts.find((p) => p.category === categoryId);
    if (firstInCategory) {
      navigate(`/products?cat=${categoryId}`);
      return;
    }
    navigate("/");
    window.setTimeout(() => {
      const el = document.querySelector("#products");
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }, 280);
  };

  const getCols = (count: number) => {
    if (count === 1) return "grid-cols-1 max-w-sm mx-auto";
    if (count === 2) return "grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto";
    if (count === 3) return "grid-cols-1 sm:grid-cols-3";
    return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4";
  };

  return (
    <section id="products" className="py-20 md:py-28 bg-xh-bg">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/5 mb-4">
            <i className="ri-apps-line text-cyan-400 text-sm" />
            <span className="text-cyan-400 text-xs font-semibold tracking-widest uppercase">
              {lang === "zh" ? "核心业务" : "Core Business"}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {lang === "zh" ? "产品与服务" : "Products & Services"}
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto text-sm md:text-base">
            {lang === "zh"
              ? "从研发设计到生产制造，提供航空动力全链条解决方案"
              : "Full-chain aerospace propulsion solutions from R&D to manufacturing"}
          </p>
        </div>

        {/* Cards Grid */}
        <div className={`grid gap-5 md:gap-6 ${getCols(products.length)}`}>
          {products.map((prod, i) => (
            <ProductCard
              key={i}
              prod={prod}
              index={i}
              lang={lang}
              hovered={hovered}
              onHover={setHovered}
              onClick={() => goToMappedSubProject(i)}
            />
          ))}
        </div>

        {/* View All CTA */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate("/products")}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl border border-cyan-500/30 bg-cyan-500/5 text-cyan-400 text-sm font-semibold hover:bg-cyan-500/15 hover:border-cyan-500/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-apps-2-line" />
            {lang === "zh" ? "浏览全部产品" : "Browse All Products"}
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
}
